import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@/fontawesome';
import { CARD_SURFACE_CLASS } from '@/components/ui/Card';
import EmptyState from '@/components/ui/EmptyState';

export type DataTableAlign = 'left' | 'right' | 'center';

export interface DataTableColumn<T> {
  key: string;
  header: string;
  align?: DataTableAlign;
  className?: string;
  /** Used as the headline of the stacked mobile card. Defaults to the first column. */
  primary?: boolean;
  /** Rendered in the mobile card's footer and never wrapped in the row link. */
  actions?: boolean;
  /** Left out of the mobile card entirely. */
  hideOnMobile?: boolean;
  render: (row: T) => ReactNode;
}

interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  rows: T[];
  keyExtractor: (row: T) => string;
  /** Makes every non-action cell a real `<Link>` so rows are middle-clickable. */
  rowHref?: (row: T) => string;
  onRowClick?: (row: T) => void;
  emptyState?: ReactNode;
  loading?: boolean;
  /** Drops the card surface - use when the table already sits inside a `Card`. */
  bare?: boolean;
  className?: string;
}

const ALIGN_CLASSES: Record<DataTableAlign, string> = {
  left: 'text-left',
  right: 'text-right',
  center: 'text-center',
};

const CELL_PADDING = 'px-3 py-2';

export default function DataTable<T>({
  columns,
  rows,
  keyExtractor,
  rowHref,
  onRowClick,
  emptyState,
  loading = false,
  bare = false,
  className = '',
}: DataTableProps<T>) {
  const surface = bare ? '' : CARD_SURFACE_CLASS;

  if (loading) {
    return (
      <div className={`${surface} ${className}`}>
        <div className="text-center py-16" role="status" aria-label="Lädt">
          <FontAwesomeIcon icon={faSpinner} className="w-6 h-6 text-gray-400 animate-spin" />
        </div>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className={`${surface} ${className}`}>
        {emptyState ?? <EmptyState title="Keine Einträge" />}
      </div>
    );
  }

  const primaryIndex = Math.max(columns.findIndex((c) => c.primary === true), 0);
  const primaryColumn = columns[primaryIndex];
  const actionColumns = columns.filter((c) => c.actions === true);
  const detailColumns = columns.filter(
    (c, idx) => idx !== primaryIndex && c.actions !== true && c.hideOnMobile !== true,
  );

  const cellContent = (column: DataTableColumn<T>, row: T, isFirstLinked: boolean): ReactNode => {
    const inner = column.render(row);
    if (rowHref === undefined || column.actions === true) {
      return <div className={CELL_PADDING}>{inner}</div>;
    }
    return (
      // Only the first linked cell is a tab stop - otherwise every row would eat a dozen of them.
      <Link to={rowHref(row)} className={`block ${CELL_PADDING}`} tabIndex={isFirstLinked ? undefined : -1}>
        {inner}
      </Link>
    );
  };

  return (
    <div className={className}>
      {/* Desktop: a real table */}
      <div className={`hidden md:block ${surface} overflow-x-auto`}>
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
              {columns.map((column) => (
                <th
                  key={column.key}
                  scope="col"
                  className={`${CELL_PADDING} font-medium ${ALIGN_CLASSES[column.align ?? 'left']}`}
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={keyExtractor(row)}
                onClick={onRowClick === undefined ? undefined : () => onRowClick(row)}
                className={`border-b border-gray-100 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/30 ${
                  onRowClick === undefined ? '' : 'cursor-pointer'
                }`}
              >
                {columns.map((column, idx) => (
                  <td
                    key={column.key}
                    className={`align-middle ${ALIGN_CLASSES[column.align ?? 'left']} ${column.className ?? ''}`}
                  >
                    {cellContent(column, row, idx === 0)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile: stacked cards instead of a horizontally scrolling table */}
      <div className={bare ? 'md:hidden divide-y divide-gray-100 dark:divide-gray-700/50' : 'md:hidden space-y-3'}>
        {rows.map((row) => (
          <div
            key={keyExtractor(row)}
            onClick={onRowClick === undefined ? undefined : () => onRowClick(row)}
            className={`${bare ? '' : CARD_SURFACE_CLASS} p-3 ${onRowClick === undefined ? '' : 'cursor-pointer'}`}
          >
            <div className="font-medium text-gray-900 dark:text-white">
              {rowHref === undefined || primaryColumn.actions === true ? (
                primaryColumn.render(row)
              ) : (
                <Link to={rowHref(row)} className="block cursor-pointer">
                  {primaryColumn.render(row)}
                </Link>
              )}
            </div>
            {detailColumns.length > 0 && (
              <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-sm">
                {detailColumns.map((column) => (
                  <div key={column.key} className="contents">
                    <dt className="text-gray-500 dark:text-gray-400">{column.header}</dt>
                    <dd className="text-right text-gray-700 dark:text-gray-300">{column.render(row)}</dd>
                  </div>
                ))}
              </dl>
            )}
            {actionColumns.length > 0 && (
              <div className="mt-3 pt-2 border-t border-gray-100 dark:border-gray-700/50 flex justify-end gap-1">
                {actionColumns.map((column) => (
                  <div key={column.key}>{column.render(row)}</div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
